import React, { useEffect, useState } from "react";
import {
  Play,
  ArrowRight,
  ZoomIn,
  X,
  ChevronLeft,
  ChevronRight,
} from "lucide-react";

export const Gallery = () => {
  const [galleryData, setGalleryData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);

  // Lightbox
  const [selectedIndex, setSelectedIndex] = useState(null);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        setLoading(true);

        const firstRes = await fetch(
          "https://webadmin.rigve.com/api/getgallery?type=0&page=1",
        );

        const firstData = await firstRes.json();

        if (firstData.status === 1) {
          let allItems = firstData.data?.data || [];
          const totalPages = firstData.data?.last_page || 1;

          if (totalPages > 1) {
            const pagePromises = [];
            for (let p = 2; p <= totalPages; p++) {
              pagePromises.push(
                fetch(
                  `https://webadmin.rigve.com/api/getgallery?type=0&page=${p}`,
                ).then((res) => res.json()),
              );
            }
            const results = await Promise.all(pagePromises);
            results.forEach((res) => {
              if (res.status === 1 && res.data?.data) {
                allItems = [...allItems, ...res.data.data];
              }
            });
          }

          setGalleryData(allItems);
        }
      } catch (error) {
        console.log(error);
      } finally {
        setLoading(false);
      }
    };

    fetchGallery();
  }, []);

  // Open Modal
  const openModal = (index) => {
    setSelectedIndex(index);
    document.body.style.overflow = "hidden";
  };

  // Close Modal
  const closeModal = () => {
    setSelectedIndex(null);
    document.body.style.overflow = "auto";
  };

  // Next Slide
  const nextSlide = () => {
    setSelectedIndex((prev) =>
      prev === galleryData.length - 1 ? 0 : prev + 1,
    );
  };

  // Previous Slide
  const prevSlide = () => {
    setSelectedIndex((prev) =>
      prev === 0 ? galleryData.length - 1 : prev - 1,
    );
  };

  // Keyboard Navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (selectedIndex === null) return;

      if (e.key === "Escape") closeModal();
      if (e.key === "ArrowRight") nextSlide();
      if (e.key === "ArrowLeft") prevSlide();
    };

    window.addEventListener("keydown", handleKeyDown);

    return () => {
      window.removeEventListener("keydown", handleKeyDown);
    };
  }, [selectedIndex, galleryData.length]);

  // Cleanup
  useEffect(() => {
    return () => {
      document.body.style.overflow = "auto";
    };
  }, []);

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#f8f5f0]">
        <h2 className="text-2xl font-semibold text-gray-700">
          Loading Gallery...
        </h2>
      </div>
    );
  }

  return (
    <>
      <section className="bg-gradient-to-b from-[#fffaf5] via-[#fdf7ef] to-[#f8f3eb] min-h-screen py-8 md:py-10">
        <div className="container mx-auto px-4 max-w-7xl">
          {/* Header */}
          <div className="text-center max-w-3xl mx-auto mb-10">
            <span className="px-5 py-2 bg-orange-100 border border-orange-300 rounded-full text-orange-600 text-xs uppercase tracking-[4px]">
              Premium Collection
            </span>
            <h1 className="mt-6 text-2xl font-bold text-gray-900 leading-tight">
              Creative Wall Texture Gallery
            </h1>
            {/* <p className="mt-4 max-w-lg mx-auto text-gray-600">
              Luxury finishes, decorative walls, concrete concepts, premium
              interiors and real project transformations.
            </p> */}
          </div>

          {/* Gallery */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {galleryData.map((item, index) => (
              <div
                key={item.id || index}
                onClick={() => openModal(index)}
                className="group rounded-2xl overflow-hidden bg-white shadow-lg hover:shadow-2xl transition-all duration-500 cursor-pointer"
              >
                <div className="relative overflow-hidden">
                  {item.type === 1 ? (
                    <img
                      src={item.image}
                      alt=""
                      className="w-full h-[420px] object-cover group-hover:scale-110 transition-all duration-700"
                    />
                  ) : (
                    <video
                      muted
                      autoPlay
                      loop
                      playsInline
                      className="w-full h-[420px] object-cover group-hover:scale-110 transition-all duration-700"
                    >
                      <source src={item.image} type="video/mp4" />
                    </video>
                  )}

                  <div className="absolute inset-0 bg-black/0 group-hover:bg-black/30 transition-all duration-300 flex items-center justify-center">
                    <ZoomIn
                      size={24}
                      className="text-white opacity-0 group-hover:opacity-100 transition-all duration-300"
                    />
                  </div>

                  <div className="absolute inset-0 bg-gradient-to-t from-black/70 to-transparent" />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Lightbox */}
      {selectedIndex !== null && (
        <div
          className="fixed inset-0 z-[9999] bg-black/95 backdrop-blur-md flex items-center justify-center"
          onClick={closeModal}
        >
          {/* Close */}
          <button
            onClick={closeModal}
            className="absolute top-5 right-5 z-50 bg-white text-black rounded-full p-3"
          >
            <X size={24} />
          </button>

          {/* Previous */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              prevSlide();
            }}
            className="absolute left-4 md:left-8 z-50 p-3 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center"
          >
            <ChevronLeft size={24} />
          </button>

          {/* Next */}
          <button
            onClick={(e) => {
              e.stopPropagation();
              nextSlide();
            }}
            className="absolute right-4 md:right-8 z-50 p-3 rounded-full bg-white/20 backdrop-blur-md text-white flex items-center justify-center"
          >
            <ChevronRight size={24} />
          </button>

          {/* Content */}
          <div
            className="max-w-7xl max-h-[90vh] px-4"
            onClick={(e) => e.stopPropagation()}
          >
            {galleryData[selectedIndex]?.type === 1 ? (
              <img
                key={galleryData[selectedIndex]?.image}
                src={galleryData[selectedIndex]?.image}
                alt=""
                className="max-h-[90vh] max-w-full object-contain rounded-2xl"
              />
            ) : (
              <video
                key={galleryData[selectedIndex]?.image}
                controls
                autoPlay
                muted
                className="max-h-[90vh] max-w-full rounded-2xl"
              >
                <source
                  src={galleryData[selectedIndex]?.image}
                  type="video/mp4"
                />
              </video>
            )}
          </div>
        </div>
      )}
    </>
  );
};
